/**
 * useEmailCreativeAnalyticsData
 *
 * DASH-EMAIL-012A: cut over from emailCreativeAnalyticsMockAdapter.js to
 * the real Email Marketing read API (emailMarketingApi.js,
 * EMAIL-CATALOG-007A). Retires the DASH-EMAIL-011a/b "fetch all four
 * subview shapes together" strategy — that was a reasonable strategy for a
 * free, synchronous mock, but is needless real-API fan-out once every
 * subview switch is a genuine network round-trip. This hook now fetches
 * ONLY the active subview's projection (`subview` is a new required
 * option), keyed on real query params it actually supports:
 *
 *   overview  -> GET creative-analytics/overview        (no filters)
 *   angles    -> GET creative-analytics/angles           (no filters)
 *   creatives -> GET creative-analytics/creatives        (program/campaign/angle)
 *                or GET creatives/{id}                   (when a Creative is selected)
 *   assets    -> GET creative-analytics/assets/{family}  (family only)
 *                or GET assets/{family}/{id}/versions/{v} (when an Asset is selected)
 *
 * Program/Campaign/Angle filtering is real ONLY for the Creatives subview
 * — the real Overview/Angles/Assets projections accept no such params
 * (verified directly against email_marketing_catalog.py's route
 * signatures, not assumed). This hook does not attempt to reconstruct that
 * filtering client-side for Overview/Angles (would produce a
 * client-computed aggregate the real catalog service never authored,
 * exactly what this cutover exists to avoid) — the caller (EmailCreativeView)
 * is responsible for disclosing this scope limitation to the user, not this
 * hook faking it.
 *
 * `environment` is gone entirely — the real Broadcast model has no such
 * field (see EMAIL-CATALOG-007A's own explicit deferral), so there is
 * nothing to send. `period` is likewise never sent — it was already a
 * pre-existing no-op against the mock adapter (destructured but unused in
 * every one of its query functions) and remains one now; retained only as
 * a passthrough value for the (unchanged) filter bar to keep rendering,
 * per DASH-EMAIL-012A's explicit instruction not to expand this checkpoint
 * into a shared filter-architecture change.
 *
 * Reference/lookup data (Campaign names, Angle names, the
 * Program->Campaign hierarchy for the filter bar) is NOT owned by this
 * hook — it has its own independent lifecycle (fetched once, rarely
 * changes) unrelated to "which subview is active," and is fetched directly
 * in EmailCreativeView.jsx / EmailDashboardPanel.jsx instead. This hook
 * owns exactly what DASH-EMAIL-012A's Frozen Decision 4 specifies: loading,
 * error, active-subview fetch, filter-to-query mapping, Creative detail
 * fetch, Asset detail fetch — no backend analytical/business logic is
 * reimplemented here, every function called is a direct real endpoint.
 */

import { useState, useEffect, useCallback } from "react";
import {
  getCreativeAnalyticsOverview,
  getCreativeAnalyticsAngles,
  getCreativeAnalyticsCreatives,
  getCreativeDetail,
  getCreativeAnalyticsAssets,
  getAssetVersionDetail,
  extractApiErrorMessage,
} from "../lib/emailMarketingApi";

const fetchForSubview = async ({
  subview,
  program,
  campaign,
  angle,
  assetFamily,
  selectedCreativeId,
  selectedAssetFamily,
  selectedAssetId,
  selectedAssetVersion,
}) => {
  if (subview === "overview") {
    return { overview: await getCreativeAnalyticsOverview() };
  }
  if (subview === "angles") {
    return { angles: await getCreativeAnalyticsAngles() };
  }
  if (subview === "creatives") {
    if (selectedCreativeId) {
      return { creativeDetail: await getCreativeDetail(selectedCreativeId) };
    }
    return { creatives: await getCreativeAnalyticsCreatives({ programId: program, campaignId: campaign, angleId: angle }) };
  }
  if (subview === "assets") {
    if (selectedAssetFamily && selectedAssetId && selectedAssetVersion) {
      return { assetDetail: await getAssetVersionDetail(selectedAssetFamily, selectedAssetId, selectedAssetVersion) };
    }
    if (assetFamily) {
      return { assets: await getCreativeAnalyticsAssets(assetFamily) };
    }
    return { assets: [] };
  }
  return {};
};

export function useEmailCreativeAnalyticsData(options = {}) {
  const {
    subview,
    period = "mtd",
    program = "all",
    campaign = "all",
    angle = "all",
    // Local to the Assets subview (not part of the shared applied filter
    // state) — passed through so switching the family selector re-fetches
    // only the Assets slice's underlying data, via the same hook.
    assetFamily = null,
    // Local to Creative Analytics' own drill-down state (owned by
    // EmailCreativeView) — primitives, not one {family,id,version} object,
    // so an inline object literal from the caller never defeats
    // fetchData's dependency array.
    selectedCreativeId = null,
    selectedAssetFamily = null,
    selectedAssetId = null,
    selectedAssetVersion = null,
  } = options;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchForSubview({
        subview,
        program,
        campaign,
        angle,
        assetFamily,
        selectedCreativeId,
        selectedAssetFamily,
        selectedAssetId,
        selectedAssetVersion,
      });
      setData(result);
    } catch (err) {
      setError(extractApiErrorMessage(err, "Failed to load Creative Analytics data"));
    } finally {
      setLoading(false);
    }
  }, [
    subview,
    program,
    campaign,
    angle,
    assetFamily,
    selectedCreativeId,
    selectedAssetFamily,
    selectedAssetId,
    selectedAssetVersion,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    loading,
    error,
    period,
    overview: data.overview || null,
    angles: data.angles || [],
    creatives: data.creatives || [],
    assets: data.assets || [],
    creativeDetail: data.creativeDetail || null,
    assetDetail: data.assetDetail || null,
    refresh: fetchData,
  };
}

export default useEmailCreativeAnalyticsData;
